import { useEffect, useRef, useState } from "react";
import { IBasket } from "../models/IBasket";
import { IFood, IFoodMenuUpgrade } from "../models/IFood";
import { IMenuUpgrade } from "../models/IOrder";
import { S3_IMAGE_BASE_URL } from "../services/config";
import { BASKET_EVENT } from "../utils/BasketContext";
import { useLanguage } from "../context/LanguageContext";

interface Props {
  food: IFood | null;
  minAmount: number;
}

function FoodDetailPopup({ food, minAmount }: Props) {
  const { t, lang } = useLanguage();
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState("");
  const [selectedUpgrades, setSelectedUpgrades] = useState<Record<string, number>>({});
  const [showConflict, setShowConflict] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!food) {
      setSelectedUpgrades({});
      return;
    }
    setQuantity(1);
    setNote("");
    // Auto-select extras with maxQuantity===1 and price===0
    const autoSelected: Record<string, number> = {};
    food.menuUpgrade
      .filter((opt) => opt.menuUpgradeIsEnabled)
      .forEach((opt) => {
        if (opt.menuUpgradeMaxQuantity === 1 && opt.menuUpgradePrice === 0) {
          autoSelected[opt.id] = 1;
        }
      });
    setSelectedUpgrades(autoSelected);
    setShowConflict(false);
  }, [food]);

  const enabledUpgrades = food
    ? food.menuUpgrade.filter((opt) => opt.menuUpgradeIsEnabled)
    : [];

  const extraCost = enabledUpgrades.reduce(
    (s, opt) => s + opt.menuUpgradePrice * (selectedUpgrades[opt.id] ?? 0),
    0
  );

  // Menu upgrades are never discounted — only the dish itself.
  const unitPrice = food ? (food.hasDiscount && food.discountedPrice != null ? food.discountedPrice : food.foodPrice) : 0;
  const total = food ? (unitPrice + extraCost) * quantity : 0;

  const TWO_HOURS = 2 * 60 * 60 * 1000;

  const toggleUpgrade = (opt: IFoodMenuUpgrade, checked: boolean) => {
    setSelectedUpgrades((prev) => ({ ...prev, [opt.id]: checked ? 1 : 0 }));
  };

  const changeUpgradeQty = (opt: IFoodMenuUpgrade, delta: number) => {
    setSelectedUpgrades((prev) => ({
      ...prev,
      [opt.id]: Math.min(
        opt.menuUpgradeMaxQuantity,
        Math.max(0, (prev[opt.id] ?? 0) + delta)
      ),
    }));
  };

  const doAdd = (forceReplace = false) => {
    if (!food) return;

    const stored = localStorage.getItem("basket");
    let basket: IBasket;
    try {
      basket = stored
        ? JSON.parse(stored)
        : { restourantId: food.restourantId, lastUpdated: Date.now(), minAmount, items: [] };
    } catch {
      basket = { restourantId: food.restourantId, lastUpdated: Date.now(), minAmount, items: [] };
    }

    const expired = basket.lastUpdated && Date.now() - basket.lastUpdated > TWO_HOURS;
    const conflict = basket.restourantId !== food.restourantId && basket.items.length > 0 && !expired;

    if (conflict && !forceReplace) {
      setShowConflict(true);
      return;
    }

    if (basket.restourantId !== food.restourantId || expired) {
      basket = { restourantId: food.restourantId, lastUpdated: Date.now(), minAmount, items: [] };
    }

    basket.minAmount = minAmount;

    const upgrades: IMenuUpgrade[] = enabledUpgrades
      .filter((opt) => (selectedUpgrades[opt.id] ?? 0) > 0)
      .map((opt) => ({
        menuUpgradeTitle: opt.menuUpgradeTitle,
        menuUpgradeQuantity: selectedUpgrades[opt.id],
        menuUpgradePrice: opt.menuUpgradePrice,
      }));

    basket.items.push({
      foodId: food.id,
      foodName: lang === "en" ? food.foodNameEn : food.foodNameTr,
      foodImagePath: food.foodImagePath,
      foodPrice: food.foodPrice,
      discountedPrice: food.hasDiscount ? food.discountedPrice : undefined,
      foodDiscountId: food.hasDiscount ? food.activeDiscount?.id : undefined,
      foodQuantity: quantity,
      foodNote: note,
      menuUpgrade: upgrades,
    });
    basket.lastUpdated = Date.now();

    localStorage.setItem("basket", JSON.stringify(basket));
    window.dispatchEvent(new CustomEvent(BASKET_EVENT));
    setShowConflict(false);
    closeButtonRef.current?.click();
  };

  const handleAddToBasket = () => doAdd(false);

  return (
    <div
      className="modal fade"
      id="foodDetailModal"
      tabIndex={-1}
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content rounded-4 shadow">
          <div className="modal-header border-0 pb-0">
            <button
              ref={closeButtonRef}
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label={t("common.close")}
            />
          </div>

          <div className="modal-body px-4 pb-4">
            {food && (
              <>
                {food.foodImagePath && (
                  <div className="text-center mb-3">
                    <img
                      src={S3_IMAGE_BASE_URL + food.foodImagePath}
                      alt={lang === "en" ? food.foodNameEn : food.foodNameTr}
                      className="food-detail-img"
                    />
                  </div>
                )}

                <p className="mb-1">
                  <strong>{t("food.name")}</strong> {lang === "en" ? food.foodNameEn : food.foodNameTr}
                </p>
                <p className="mb-1">
                  <strong>{t("food.price")}</strong>{" "}
                  {food.hasDiscount && food.discountedPriceText ? (
                    <>
                      <s className="text-muted">
                        {food.foodPriceText} {food.currency.currencyDisplayText}
                      </s>{" "}
                      <span className="text-danger fw-bold">
                        {food.discountedPriceText} {food.currency.currencyDisplayText}
                      </span>
                      {food.activeDiscount?.description && (
                        <span className="badge bg-warning text-dark ms-2">
                          <i className="bi bi-percent me-1"></i>
                          {food.activeDiscount.description}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-danger">
                      {food.foodPriceText} {food.currency.currencyDisplayText}
                    </span>
                  )}
                </p>
                {food.foodContents && (
                  <p className="mb-1">
                    <strong>{t("food.contents")}</strong> {food.foodContents}
                  </p>
                )}

                {/* Ekstralar */}
                {enabledUpgrades.length > 0 && (
                  <div className="mt-3 mb-1">
                    <p className="mb-2 fw-semibold">{t("food.extras")}</p>
                    {enabledUpgrades.map((opt) => (
                      <div key={opt.id} className="mb-2">
                        {opt.menuUpgradeMaxQuantity === 1 ? (
                          /* Checkbox — tek seçim */
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`upgrade-${opt.id}`}
                              checked={(selectedUpgrades[opt.id] ?? 0) === 1}
                              onChange={(e) => toggleUpgrade(opt, e.target.checked)}
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`upgrade-${opt.id}`}
                            >
                              {opt.menuUpgradeTitle}
                              {opt.menuUpgradePrice > 0 && (
                                <span className="text-danger ms-1 small">
                                  +{opt.menuUpgradePriceText}₺
                                </span>
                              )}
                            </label>
                          </div>
                        ) : (
                          /* Counter — çoklu seçim */
                          <div>
                            <div className="d-flex align-items-center justify-content-between">
                              <span>
                                {opt.menuUpgradeTitle}
                                {opt.menuUpgradePrice > 0 && (
                                  <span className="text-danger ms-1 small">
                                    +{opt.menuUpgradePriceText}₺
                                  </span>
                                )}
                              </span>
                              <div className="d-flex align-items-center gap-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary rounded-circle food-detail-qty-btn"
                                  onClick={() => changeUpgradeQty(opt, -1)}
                                >
                                  <i className="bi bi-dash"></i>
                                </button>
                                <span
                                  className="fw-bold"
                                  style={{ minWidth: "16px", textAlign: "center" }}
                                >
                                  {selectedUpgrades[opt.id] ?? 0}
                                </span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary rounded-circle food-detail-qty-btn"
                                  onClick={() => changeUpgradeQty(opt, 1)}
                                >
                                  <i className="bi bi-plus"></i>
                                </button>
                              </div>
                            </div>
                            {(selectedUpgrades[opt.id] ?? 0) > 0 &&
                              opt.menuUpgradePrice > 0 && (
                                <small className="text-muted">
                                  {selectedUpgrades[opt.id]} x {opt.menuUpgradePriceText}₺
                                  {" "}={" "}
                                  {(
                                    (selectedUpgrades[opt.id] ?? 0) * opt.menuUpgradePrice
                                  ).toFixed(2)}
                                  ₺
                                </small>
                              )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Cross-restaurant uyarısı */}
                {showConflict && (
                  <div className="alert alert-warning py-2 mt-3 mb-0 small">
                    <p className="mb-2">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      {t("food.conflictWarning")}
                    </p>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-danger rounded-pill px-3"
                        onClick={() => doAdd(true)}
                      >
                        {t("food.conflictConfirm")}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                        onClick={() => setShowConflict(false)}
                      >
                        {t("common.cancel")}
                      </button>
                    </div>
                  </div>
                )}

                <p className="mb-1 mt-3">
                  <strong>{t("food.customerNote")}</strong>
                </p>
                <input
                  type="text"
                  className="form-control mb-4"
                  placeholder="Note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />

                <div className="d-flex align-items-center justify-content-between gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <button
                      className="btn btn-outline-secondary rounded-circle food-detail-qty-btn"
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    >
                      <i className="bi bi-dash"></i>
                    </button>
                    <span className="fw-bold fs-5 food-detail-qty">{quantity}</span>
                    <button
                      className="btn btn-outline-secondary rounded-circle food-detail-qty-btn"
                      onClick={() => setQuantity((q) => q + 1)}
                    >
                      <i className="bi bi-plus"></i>
                    </button>
                  </div>

                  <span className="fw-bold small">
                    {t("food.totalPrice")} {total.toFixed(2)}{" "}
                    {food.currency.currencyDisplayText}
                  </span>

                  <button
                    className="btn btn-success rounded-pill px-3"
                    onClick={handleAddToBasket}
                  >
                    {t("food.addToCart")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FoodDetailPopup;
